import { ChangeDetectionStrategy, Component, computed, effect, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';

import { MatTableModule } from '@angular/material/table';
import { MatSortModule, Sort } from '@angular/material/sort';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';

import { SiteService } from '../../../core/services/site.service';
import { SiteManagementDepartmentService } from '../../../core/services/site-management-department.service';
import { LoadingService } from '../../../core/services/loading.service';
import { NotificationService } from '../../../core/services/notification.service';
import { SiteListItem } from '../../../shared/interfaces';
import { getWbgtStatusBadgeClass, getWindStatusBadgeClass } from '../../../shared/utils/sensor-status';

export interface SiteFilterCriteria {
  name: string;
  departmentId: string | null;
  wbgtStatus: string | null;
  windSpeedStatus: string | null;
}

@Component({
  selector: 'app-site-list',
  imports: [
    FormsModule,
    MatTableModule,
    MatSortModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatIconModule,
    MatButtonModule,
  ],
  templateUrl: './site-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SiteListComponent {
  private siteService = inject(SiteService);
  private siteManagementDepartmentService = inject(SiteManagementDepartmentService);
  private loadingService = inject(LoadingService);
  private notificationService = inject(NotificationService);
  private router = inject(Router);

  // Filter State
  public filterCriteria = signal<SiteFilterCriteria>({
    name: '',
    departmentId: null,
    wbgtStatus: null,
    windSpeedStatus: null,
  });

  // Sort State
  public sortState = signal<Sort>({ active: '', direction: '' });

  // Expose departments for select box
  public departments = this.siteManagementDepartmentService.departments;

  // Table Columns
  public displayedColumns = [
    'name',
    'managementDepartmentName',
    'wbgtStatus',
    'wbgtValue',
    'windSpeedStatus',
    'windSpeedValue',
    'windDirection'
  ];

  // Computed data source combining sites, departments, applying filters and sort
  public dataSource = computed<SiteListItem[]>(() => {
    const sites = this.siteService.siteListItems();
    const filter = this.filterCriteria();
    const sort = this.sortState();

    // 1. (Department name mapping is now handled in SiteService)
    let processedSites = sites;

    // 2. Apply filters
    processedSites = processedSites.filter(site => {
      const matchName = !filter.name || site.name.toLowerCase().includes(filter.name.toLowerCase());
      const matchDept = !filter.departmentId || site.managementDepartmentId === filter.departmentId;
      const matchWbgt = !filter.wbgtStatus || site.wbgtStatus === filter.wbgtStatus;
      const matchWind = !filter.windSpeedStatus || site.windSpeedStatus === filter.windSpeedStatus;
      return matchName && matchDept && matchWbgt && matchWind;
    });

    // 3. Apply sort
    if (sort.active && sort.direction !== '') {
      processedSites.sort((a, b) => {
        const isAsc = sort.direction === 'asc';
        let valA: string | number = '';
        let valB: string | number = '';

        switch (sort.active) {
          case 'name':
            valA = a.name; valB = b.name; break;
          case 'managementDepartmentName':
            valA = a.managementDepartmentName || ''; valB = b.managementDepartmentName || ''; break;
          case 'wbgtStatus':
            valA = a.wbgtStatus || ''; valB = b.wbgtStatus || ''; break;
          case 'wbgtValue':
            valA = a.wbgtValue ?? -Infinity; valB = b.wbgtValue ?? -Infinity; break;
          case 'windSpeedStatus':
            valA = a.windSpeedStatus || ''; valB = b.windSpeedStatus || ''; break;
          case 'windSpeedValue':
            valA = a.windSpeedValue ?? -Infinity; valB = b.windSpeedValue ?? -Infinity; break;
          case 'windDirection':
            valA = a.windDirection || ''; valB = b.windDirection || ''; break;
        }

        if (valA < valB) {
          return isAsc ? -1 : 1;
        }
        if (valA > valB) {
          return isAsc ? 1 : -1;
        }
        return 0;
      });
    }

    return processedSites;
  });

  constructor() {
    let wasLoading = false;
    effect(() => {
      const isLoading = this.siteService.isLoading() || this.siteManagementDepartmentService.isLoading();
      if (isLoading && !wasLoading) {
        this.loadingService.show();
        wasLoading = true;
      } else if (!isLoading && wasLoading) {
        this.loadingService.hide();
        wasLoading = false;
      }
    });

    effect(() => {
      const siteError = this.siteService.error();
      if (siteError) {
        this.notificationService.error(siteError);
      }
    });

    effect(() => {
      const deptError = this.siteManagementDepartmentService.error();
      if (deptError) {
        this.notificationService.error(deptError);
      }
    });
  }

  public onSortChange(sort: Sort): void {
    this.sortState.set(sort);
  }

  public onRowClick(site: SiteListItem): void {
    this.router.navigate(['/sites/detail', site.id]);
  }

  public updateFilter<K extends keyof SiteFilterCriteria>(key: K, value: SiteFilterCriteria[K]): void {
    this.filterCriteria.update(criteria => ({
      ...criteria,
      [key]: value
    }));
  }

  public clearFilters(): void {
    this.filterCriteria.set({
      name: '',
      departmentId: null,
      wbgtStatus: null,
      windSpeedStatus: null,
    });
  }

  public getWbgtBadgeClass(statusNo: string | undefined): string {
    return getWbgtStatusBadgeClass(statusNo);
  }

  public getWindBadgeClass(statusNo: string | undefined): string {
    return getWindStatusBadgeClass(statusNo);
  }

  public getStatusLabel(status: string | undefined): string {
    switch (status) {
      case 'normal': return 'ほぼ安全';
      case 'caution': return '注意';
      case 'alert': return '警戒';
      case 'danger': return '危険';
      case 'stopped': return '停止';
      default: return '未設定';
    }
  }
}

